import { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useTranslation } from "react-i18next";
import { AddToCartNotifier } from "../../Components/CartComponents";
import { CartsContext } from "../../Context/CartsContext";

export default function CartItem({ item, index, pathname }) {

    const { t } = useTranslation();
    const dir = localStorage.getItem("dir");
    const MySwal = withReactContent(Swal);

    const { QtyUpdate, DeleteCartItem, GetStockPolicyItem } = useContext(CartsContext);

    const [tempQty, setTempQty] = useState(item.item_qty);
    const [policyMinQty, setPolicyMinQty] = useState(1);

    useEffect(() => {
        setTempQty(item.item_qty);
    }, [item.item_qty]);

    useEffect(() => {
        const loadStockPolicy = async () => {
            try {
                const stockPolicy = await GetStockPolicyItem(
                    item?.item_number,
                    item?.stock_oe,
                    item?.item_sup_id,
                    item?.business_cart_item_brand
                );
                const minQty = Number(stockPolicy?.policy_min_qty || 1);
                setPolicyMinQty(minQty);
            } catch (error) {
                console.log("Error fetching stock policy:", error);
                setPolicyMinQty(1);
            }
        };
        loadStockPolicy();
    }, [item?.item_number, item?.stock_oe, item?.item_sup_id, item?.business_cart_item_brand]);

    /* ================= DELETE ITEM ================= */
    const handleDeleteCartItem = async (item_uid) => {
        const result = await MySwal.fire({
            title: "Remove Item",
            text: "Are you sure you want to remove this item?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes Delete",
            cancelButtonText: "Cancel!",
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            await DeleteCartItem(item_uid);
            toast.success("Item Removed successfully!", {
                position: toast.POSITION.BOTTOM_CENTER,
            });
        }
    };

    const handleQtyCommit = async (item_uid, value) => {
        let qty = Number(value);

        const available_stock = Number(
            item?.available_qty ??
            item?.max_qty ??
            item?.VD_QTY ??
            0
        );

        try {
            const minQty = Number(policyMinQty || 1);

            // Invalid or less than minimum
            if (isNaN(qty) || qty < minQty) {
                qty = minQty;
            }

            // Always make quantity a multiple of minQty
            if (qty % minQty !== 0) {
                qty = Math.ceil(qty / minQty) * minQty;
            }

            // Stock validation
            if (qty > available_stock) {

                toast.success(
                    <AddToCartNotifier
                        item_name={t("sorry_we_have_only", {
                            value: available_stock,
                        })}
                    />
                );

                // Maximum possible multiple within stock
                qty = Math.floor(available_stock / minQty) * minQty;

                // If stock is less than minimum
                if (qty < minQty) {
                    qty = minQty;
                }
            }

            setTempQty(qty);

            await QtyUpdate(item_uid, qty);

        } catch (error) {
            console.log(error);
        }
    };



    return (
        <div
            className="card"
            dir={dir}
            style={{
                textAlign: dir === "rtl" ? "right" : "left",
                background:
                    item?.part_available === false
                        ? "#fff5f5"
                        : "#fff",
                border:
                    item?.part_available === false
                        ? "1px solid #ffb3b3"
                        : "",
                animation:
                    item?.part_available === false
                        ? "outOfStockPulse 1s infinite"
                        : "none",
                transition: "all 0.3s ease"
            }}
        >
            <div className="row">
                {/* IMAGE */}
                <div className="col-sm-2">
                    <figure className="product-image-container ml-3 mt-1">
                        <NavLink to={item.item_url} className="product-image">
                            <img
                                width={70}
                                src={item.item_img_url}
                                alt={item.item_title}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null;
                                    currentTarget.src =
                                        "https://ghayar.ae/assets/images/prodefault.jpg";
                                }}
                            />
                        </NavLink>
                    </figure>
                </div>

                {/* DETAILS */}
                <div className="col-sm-5">
                    <div className="mt-2">{item.item_title}</div>
                    <small>
                        {t("part_number")}: {item.item_number}
                    </small>
                    <br />

                    {item?.stock_delivery_days > 0 && (
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            Delivery Days : {item.stock_delivery_days === 0 ? "Next Day" : item.stock_delivery_days}
                        </div>
                    )}

                    <NavLink to={item.item_url} style={{ display: "block" }}>
                        {t("details")}
                    </NavLink>

                    {item?.stock_message && (
                        <div
                            style={{
                                marginTop: "6px",
                                marginBottom: "6px",
                                backgroundColor: "#FFF3CD",
                                borderLeft: "3px solid #FFC107",
                                color: "#856404",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "inline-block",
                                maxWidth: "100%"
                            }}
                        >
                            <i
                                className="fas fa-exclamation-triangle"
                                style={{ marginRight: "6px" }}
                            ></i>
                            {item.stock_message}
                        </div>
                    )}

                    {/* MINIMUM QUANTITY MESSAGE BELOW DETAILS LINK - INFO COLOR */}
                    {policyMinQty > 1 && item?.part_available !== false && (
                        <div style={{
                            marginTop: '6px',
                            marginBottom: '6px',
                            backgroundColor: '#E3F2FD',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            borderLeft: '3px solid #1976D2',
                            fontSize: '12px',
                            color: '#0D47A1',
                            fontWeight: '500',
                            display: 'inline-block',
                            maxWidth: '100%'
                        }}>
                            <i className="fas fa-info-circle" style={{ color: '#1976D2', fontSize: '12px', marginRight: '6px' }}></i>
                            This product can only be ordered in multiples of <strong style={{ color: '#1565C0', fontSize: '13px' }}>{policyMinQty}</strong>
                        </div>
                    )}
                </div>

                {
                    item?.part_available === false ? (
                        <div className="col-sm-4 d-flex align-items-center justify-content-center">
                            <div
                                style={{
                                    background: "#ffdddd",
                                    color: "#d60000",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    fontWeight: "700",
                                    fontSize: "16px",
                                    border: "1px solid #ffb3b3"
                                }}
                            >
                                Out Of Stock
                            </div>
                        </div>
                    )
                        :
                        (
                            <div className="col-sm-2">
                                <div className="product-single-qty mt-2">
                                    <input
                                        type="number"
                                        className="form-control text-center"
                                        value={tempQty}
                                        min={policyMinQty}
                                        step={policyMinQty}
                                        disabled={pathname === "/basket/confirm"}
                                        inputMode="numeric"
                                        onChange={(e) => {
                                            if (pathname === "/basket/confirm") return;
                                            setTempQty(e.target.value);
                                        }}
                                        onBlur={() => {
                                            if (pathname === "/basket/confirm") return;
                                            handleQtyCommit(item?.item_uid, tempQty);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.target.blur();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )
                }

                {/* PRICE */}
                {
                    item?.part_available !== false &&
                    <div className="col-sm-2">
                        <div className="mt-2">
                            {item.total_display_price}
                            <br />
                            <small>{t("incl_vat_short")}</small>
                        </div>
                    </div>
                }

                {/* DELETE ICON */}
                {pathname !== "/basket/confirm" && (
                    <div className="col-sm-1 pt-4">
                        <NavLink
                            onClick={() =>
                                handleDeleteCartItem(item?.item_uid)
                            }
                            className="icon-cancel"
                            title="Remove Product"
                        />
                    </div>
                )}
            </div>

            <style>
                {`
            @keyframes outOfStockPulse {
                0% {
                    opacity: 0.5;
                    transform: scale(1);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(0.99);
                    }
                        100% {
                    transform: scale(1);
                    opacity: 0.5;
                }
            }
        `}
            </style>


        </div>
    );
}